import { Button } from '@ophelos/ui/components/button';
import { InputGroup, InputGroupAddon, InputGroupTextarea } from '@ophelos/ui/components/input-group';
import { ArrowUp } from 'lucide-react';
import { type KeyboardEvent, type ReactNode, useEffect, useRef, useState } from 'react';

interface ChatInputBaseProps {
  onSend: (message: string) => void;
  disabled?: boolean;
  placeholder?: string;
  /** Content rendered above the textarea (e.g., file attachment previews) */
  headerContent?: ReactNode;
  /** Extra action buttons rendered next to the send button (e.g., paperclip) */
  extraActions?: ReactNode;
}

type ChatInputProps = ChatInputBaseProps & ({ value: string; onChange: (value: string) => void } | { value?: never; onChange?: never });

export function ChatInput({
  onSend,
  disabled,
  placeholder = 'How can I help you today?',
  value: controlledValue,
  onChange: controlledOnChange,
  headerContent,
  extraActions,
}: ChatInputProps) {
  const [internalValue, setInternalValue] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    textareaRef.current?.focus();
  }, []);

  const isControlled = controlledValue !== undefined;
  const value = isControlled ? controlledValue : internalValue;
  const setValue = isControlled ? (controlledOnChange ?? (() => {})) : setInternalValue;

  const handleSend = () => {
    const trimmed = value.trim();
    if (!trimmed || disabled) return;
    onSend(trimmed);
    setValue('');
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div className="shrink-0 ">
      {headerContent}
      <InputGroup className="rounded-2xl ring-4 ring-border-light border-border-heavy/80">
        <InputGroupTextarea
          ref={textareaRef}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          rows={1}
          className="bg-transparent field-sizing-content max-h-24 hover:bg-card"
        />
        <InputGroupAddon align="block-end">
          <div className="flex items-center gap-1 ml-auto">
            {extraActions}
            <Button
              size="icon-sm"
              className="size-7 rounded-full ml-auto"
              onClick={handleSend}
              disabled={!value.trim() || disabled}
              aria-label="Send message"
            >
              <ArrowUp className="size-4" />
            </Button>
          </div>
        </InputGroupAddon>
      </InputGroup>
    </div>
  );
}
